import { useEffect, useRef, useState } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import { testimonials } from "@/data/siteContent";
import { Quote } from "lucide-react";

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const quoteRef = useRef(null);
  const total = testimonials.quotes.length;

  // Single source of truth: whenever `active` changes (via autoplay OR a dot),
  // one clean entrance animation runs. No overlapping/competing timelines.
  useEffect(() => {
    if (prefersReducedMotion() || !quoteRef.current) return undefined;
    const tween = gsap.fromTo(
      quoteRef.current,
      { opacity: 0, filter: "blur(8px)", y: 18 },
      { opacity: 1, filter: "blur(0px)", y: 0, duration: 0.7, ease: "power3.out" }
    );
    return () => tween.kill();
  }, [active]);

  // Autoplay — paused while the visitor is hovering the block.
  const pausedRef = useRef(false);
  useEffect(() => {
    const id = setInterval(() => {
      if (!pausedRef.current) setActive((a) => (a + 1) % total);
    }, 6000);
    return () => clearInterval(id);
  }, [total]);

  const current = testimonials.quotes[active];

  return (
    <section
      className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32"
      data-testid="testimonials-section"
      aria-label={testimonials.heading || "How We Think"}
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
    >
      <p
        className="mb-4 font-accent text-lg italic text-white/60"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {testimonials.eyebrow}
      </p>

      <div className="relative mt-8 min-h-[300px]">
        <Quote size={64} className="mb-8 text-gold/25" aria-hidden="true" />
        <div ref={quoteRef} aria-live="polite" aria-atomic="true">
          <blockquote
            className="max-w-4xl font-display text-3xl leading-[1.28] tracking-tight text-white sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 500 }}
          >
            {current.quote}
          </blockquote>
          <div className="mt-10">
            <div className="text-sm uppercase tracking-[0.2em] text-white">{current.name}</div>
            <div className="mt-1.5 text-sm text-white/65">{current.title}</div>
          </div>
        </div>
      </div>

      <div className="mt-12 flex items-center gap-3">
        {testimonials.quotes.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            aria-label={`Principle ${i + 1}`}
            aria-current={i === active ? "true" : undefined}
            data-testid={`testimonial-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === active ? "w-10 bg-gold" : "w-4 bg-white/25 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
